import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HelpCircle, Plus, Edit, Trash2, Upload, ArrowLeft, FileText, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { questionService } from '../../services/questionService';
import { quizService } from '../../services/quizService';
import { bookService } from '../../services/bookService';
import { authService } from '../../services/authService';
import { aiService } from '../../services/aiService';
import { useToast } from '../../hooks/use-toast';

const AdminQuestions = () => {
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const [questions, setQuestions] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiConnectionTest, setAiConnectionTest] = useState(null);
  const [aiFormData, setAiFormData] = useState({
    num_questions: 10,
    difficulty: 'Medium',
    question_prompt: ''
  });
  const [formData, setFormData] = useState({
    quiz_id: '',
    question: '',
    type: '',
    options: ['', '', '', ''],
    correct_answer: '',
    explanation: ''
  });

  useEffect(() => {
    // Check authentication
    if (!authService.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    
    if (quizId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [navigate, quizId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionsData, quizData] = await Promise.all([
        quizService.getQuizQuestions(quizId),
        quizService.getQuiz(quizId)
      ]);
      setQuestions(questionsData);
      setQuiz(quizData);
      setFormData(prev => ({ ...prev, quiz_id: quizId }));
      
      // Fetch book data if available
      if (quizData.book_id) {
        try {
          const bookData = await bookService.getBook(quizData.book_id);
          setBook(bookData);
        } catch (error) {
          console.error('Error fetching book:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load questions.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const testAIConnection = async () => {
    try {
      const result = await aiService.testConnection();
      setAiConnectionTest(result);
      return result.success;
    } catch (error) {
      setAiConnectionTest({
        success: false,
        message: "Failed to connect to AI service"
      });
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('option_')) {
      const index = parseInt(name.split('_')[1]);
      const newOptions = [...formData.options];
      newOptions[index] = value;
      setFormData({ ...formData, options: newOptions });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    
    // Reset form when question type changes
    if (name === 'type') {
      setFormData(prev => ({
        ...prev,
        type: value,
        options: value === 'multiple_choice' ? ['', '', '', ''] : [],
        correct_answer: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let questionData = {
        quiz_id: quizId,
        question: formData.question,
        type: formData.type,
        explanation: formData.explanation
      };

      if (formData.type === 'multiple_choice') {
        const validOptions = formData.options.filter(opt => opt.trim() !== '');
        if (validOptions.length < 2) {
          toast({
            title: "Error",
            description: "Multiple choice questions need at least 2 options.",
            variant: "destructive",
          });
          return;
        }
        
        questionData.options = validOptions;
        
        // Find correct answer index
        const correctIndex = validOptions.findIndex(opt => opt === formData.correct_answer);
        if (correctIndex === -1) {
          toast({
            title: "Error",
            description: "Please select a valid correct answer from the options.",
            variant: "destructive",
          });
          return;
        }
        questionData.correct_answer = correctIndex;
        
      } else if (formData.type === 'true_false') {
        if (formData.correct_answer === '') {
          toast({
            title: "Error",
            description: "Please select the correct answer for true/false question.",
            variant: "destructive",
          });
          return;
        }
        questionData.correct_answer = formData.correct_answer === 'true';
      }

      if (editingQuestion) {
        await questionService.updateQuestion(editingQuestion.id, questionData);
        toast({
          title: "Success",
          description: "Question updated successfully!",
        });
      } else {
        await questionService.createQuestion(questionData);
        toast({
          title: "Success", 
          description: "Question created successfully!",
        });
      }

      resetForm();
      fetchData();
      
    } catch (error) {
      console.error('Error saving question:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save question.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (question) => {
    setFormData({
      quiz_id: quizId,
      question: question.question,
      type: question.type,
      options: question.options || ['', '', '', ''],
      correct_answer: question.type === 'multiple_choice' 
        ? (question.options ? question.options[question.correct_answer] : '')
        : question.correct_answer.toString(),
      explanation: question.explanation || ''
    });
    setEditingQuestion(question);
    setShowAddForm(true);
  };

  const handleDelete = async (question) => {
    if (!window.confirm(`Are you sure you want to delete this question?`)) {
      return;
    }

    try {
      console.log('Deleting question with ID:', question.id);
      await questionService.deleteQuestion(question.id);
      toast({
        title: "Success",
        description: "Question deleted successfully!",
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete question.",
        variant: "destructive",
      });
    }
  };

  const handleCSVUpload = async () => {
    /* istanbul ignore next -- defensive guard; the import button is disabled without a file */
    if (!csvFile) {
      toast({
        title: "Error",
        description: "Please select a CSV file.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await questionService.importQuestionsCSV(csvFile);
      toast({
        title: "Success",
        description: `Questions imported successfully! ${result.imported_count || 'Some'} questions added.`,
      });
      setShowCSVImport(false);
      setCsvFile(null);
      fetchData();
    } catch (error) {
      console.error('Error importing CSV:', error);
      let errorMessage = "Failed to import CSV.";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.errors) {
        errorMessage = `Import errors: ${error.response.data.errors.slice(0, 3).join(', ')}`;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleAIGenerate = async () => {
    if (!book) {
      toast({
        title: "Error",
        description: "Book information is required for AI generation.",
        variant: "destructive",
      });
      return;
    }

    setAiGenerating(true);
    try {
      const generateData = {
        book_title: book.title,
        book_author: book.author,
        quiz_title: quiz.title,
        difficulty: aiFormData.difficulty,
        num_questions: aiFormData.num_questions,
        question_prompt: aiFormData.question_prompt || undefined
      };

      const result = await aiService.generateQuiz(generateData);
      
      if (result.success) {
        toast({
          title: "Success",
          description: `Generated ${result.questions_created} questions successfully!`,
        });
        
        setShowAIGenerate(false);
        setAiFormData({
          num_questions: 10,
          difficulty: 'Medium',
          question_prompt: ''
        });
        fetchData();
      } else {
        throw new Error(result.message || "Failed to generate questions");
      }
      
    } catch (error) {
      console.error('Error generating questions:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to generate questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      quiz_id: quizId,
      question: '',
      type: '',
      options: ['', '', '', ''],
      correct_answer: '',
      explanation: ''
    });
    setShowAddForm(false);
    setEditingQuestion(null);
  };

  const resetFormForNewQuestion = () => {
    setFormData({
      quiz_id: quizId,
      question: '',
      type: '',
      options: ['', '', '', ''],
      correct_answer: '',
      explanation: ''
    });
    setEditingQuestion(null);
    // Don't set showAddForm to false here!
  };

  const downloadCSVTemplate = () => {
    // Get current quiz and book info for the template
    const bookTitle = book?.title || "Your Book Title";
    const quizTitle = quiz?.title || "Your Quiz Title";
    
    const csvContent = [
      'book_title,quiz_title,description,difficulty,question,question_type,option_a,option_b,option_c,option_d,correct_answer,explanation',
      `"${bookTitle}","${quizTitle}","Quiz description","Medium","Who is the protagonist?","multiple_choice","Option A","Option B","Option C","Option D","Option B","Explanation for the correct answer"`,
      `"${bookTitle}","${quizTitle}","Quiz description","Medium","This is a true/false question","true_false","","","","","true","Explanation for true/false answer"`,
      `"${bookTitle}","${quizTitle}","Quiz description","Easy","Another multiple choice question","multiple_choice","First choice","Second choice","","","First choice","Only need 2 options minimum"`
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'questions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Success",
      description: "CSV template downloaded successfully!",
    });
  };

  if (loading) {
    return (
      <Layout isAdmin={true}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!quizId) {
    return (
      <Layout isAdmin={true}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Select a Quiz</h1>
            <p className="text-gray-600 mb-4">Please select a quiz from the quizzes page to manage its questions.</p>
            <Link to="/admin/quizzes">
              <Button>Go to Quizzes</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isAdmin={true}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin/quizzes">
              <Button variant="ghost" className="mb-4 hover:bg-blue-50">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Quizzes
              </Button>
            </Link>
            
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Questions Management</h1>
                {quiz && (
                  <div>
                    <p className="text-gray-600">Managing questions for: <span className="font-semibold">{quiz.title}</span></p>
                    {book && (
                      <p className="text-gray-500 text-sm">Book: {book.title} by {book.author}</p>
                    )}
                    <Badge className="mt-2 bg-blue-100 text-blue-800 border-blue-200">
                      {questions.length} questions
                    </Badge>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    setShowAIGenerate(true);
                    await testAIConnection();
                  }}
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate with AI
                </Button>
                <Button
                  onClick={() => setShowCSVImport(true)}
                  variant="outline"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button
                  onClick={() => {
                    setShowAddForm(true);
                    setEditingQuestion(null);
                    resetFormForNewQuestion();
                  }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Question
                </Button>
              </div>
            </div>
          </div>

          {/* AI Generate Modal */}
          {showAIGenerate && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
                  Generate Questions with Gemini AI
                </CardTitle>
                
                {/* AI Connection Status */}
                {aiConnectionTest && (
                  <div className={`flex items-center space-x-2 text-sm ${
                    aiConnectionTest.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {aiConnectionTest.success ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    <span>{aiConnectionTest.message}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">How it works:</h4>
                  <p className="text-sm text-purple-800 mb-3">
                    Using Gemini AI, we'll generate intelligent quiz questions based on "{book?.title || 'your book'}" 
                    {book?.author ? ` by ${book.author}` : ''}. The AI will create a mix of multiple-choice and true/false questions
                    with explanations, tailored to your specified difficulty level.
                  </p>
                  <p className="text-xs text-purple-700">
                    Example prompts: "Focus on character development and themes" or "Include questions about historical context"
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="num_questions">Number of Questions</Label>
                    <Select value={aiFormData.num_questions.toString()} onValueChange={(value) => setAiFormData({...aiFormData, num_questions: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 questions</SelectItem>
                        <SelectItem value="10">10 questions</SelectItem>
                        <SelectItem value="15">15 questions</SelectItem>
                        <SelectItem value="20">20 questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="difficulty">Difficulty Level</Label>
                    <Select value={aiFormData.difficulty} onValueChange={(value) => setAiFormData({...aiFormData, difficulty: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Easy">Easy</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="aiPrompt">Focus Areas (Optional)</Label>
                  <Textarea
                    id="aiPrompt"
                    value={aiFormData.question_prompt}
                    onChange={(e) => setAiFormData({...aiFormData, question_prompt: e.target.value})}
                    placeholder="e.g., Focus on character development, themes, plot twists, historical context..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAIGenerate} 
                    disabled={aiGenerating || (aiConnectionTest && !aiConnectionTest.success)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {aiGenerating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {aiGenerating ? 'Generating with AI...' : 'Generate Questions'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowAIGenerate(false);
                      setAiConnectionTest(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                
                {aiConnectionTest && aiConnectionTest.success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>✓ Connected to Gemini AI</strong><br />
                      Ready to generate intelligent questions based on your book content.
                    </p>
                  </div>
                )}
                
                {aiConnectionTest && !aiConnectionTest.success && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">
                      <strong>⚠ AI Service Issue:</strong><br />
                      {aiConnectionTest.message}. Please check your configuration or try again later.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* CSV Import Modal */}
          {showCSVImport && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle>Import Questions from CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">CSV Format Requirements:</h4>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p><strong>Required columns:</strong> book_title, quiz_title, description, difficulty, question, question_type, correct_answer</p>
                    <p><strong>Question types:</strong> "multiple_choice" or "true_false"</p>
                    <p><strong>For multiple choice:</strong> Use option_a, option_b, option_c, option_d columns. Set correct_answer to the exact text of the correct option.</p>
                    <p><strong>For true/false:</strong> Set correct_answer to "true" or "false"</p>
                    <p><strong>Difficulty:</strong> "Easy", "Medium", or "Hard"</p>
                  </div>
                  <Button
                    onClick={downloadCSVTemplate}
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100 mt-3"
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    Download Template
                  </Button>
                </div>
                
                <div>
                  <Label htmlFor="csvFile">Select CSV File</Label>
                  <Input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={handleCSVUpload} disabled={!csvFile}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Questions
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowCSVImport(false);
                      setCsvFile(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add/Edit Form */}
          {showAddForm && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle>{editingQuestion ? 'Edit Question' : 'Add New Question'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="question">Question *</Label>
                    <Textarea
                      id="question"
                      name="question"
                      value={formData.question}
                      onChange={handleInputChange}
                      rows={3}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="type">Question Type *</Label>
                    <Select value={formData.type} onValueChange={(value) => handleSelectChange('type', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select question type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                        <SelectItem value="true_false">True/False</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.type === 'multiple_choice' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="option_0">Option A *</Label>
                          <Input
                            id="option_0"
                            name="option_0"
                            value={formData.options[0]}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="option_1">Option B *</Label>
                          <Input
                            id="option_1"
                            name="option_1"
                            value={formData.options[1]}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="option_2">Option C</Label>
                          <Input
                            id="option_2"
                            name="option_2"
                            value={formData.options[2]}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div>
                          <Label htmlFor="option_3">Option D</Label>
                          <Input
                            id="option_3"
                            name="option_3"
                            value={formData.options[3]}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="correct_answer">Correct Answer *</Label>
                        <Select value={formData.correct_answer} onValueChange={(value) => handleSelectChange('correct_answer', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select correct answer" />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.options.map((option, index) => 
                              option.trim() && (
                                <SelectItem key={index} value={option}>
                                  {option}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {formData.type === 'true_false' && (
                    <div>
                      <Label htmlFor="correct_answer">Correct Answer *</Label>
                      <Select value={formData.correct_answer} onValueChange={(value) => handleSelectChange('correct_answer', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select correct answer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">True</SelectItem>
                          <SelectItem value="false">False</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="explanation">Explanation</Label>
                    <Textarea
                      id="explanation"
                      name="explanation"
                      value={formData.explanation}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Optional explanation for the answer"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="submit">
                      {editingQuestion ? 'Update Question' : 'Create Question'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Questions List */}
          <div className="space-y-4">
            {questions.map((question, index) => (
              <Card key={question.id} className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          Question {index + 1}
                        </Badge>
                        <Badge className={question.type === 'multiple_choice' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                          {question.type === 'multiple_choice' ? 'Multiple Choice' : 'True/False'}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{question.question}</h3>
                      
                      {question.type === 'multiple_choice' && question.options && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                          {question.options.map((option, optIndex) => (
                            <div 
                              key={optIndex} 
                              className={`p-2 text-sm rounded border ${
                                optIndex === question.correct_answer 
                                  ? 'bg-green-50 border-green-300 text-green-800 font-medium' 
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              {String.fromCharCode(65 + optIndex)}. {option}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {question.type === 'true_false' && (
                        <div className="mb-3">
                          <span className="text-sm text-gray-600">Correct Answer: </span>
                          <Badge className={question.correct_answer ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {question.correct_answer ? 'True' : 'False'}
                          </Badge>
                        </div>
                      )}
                      
                      {question.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                          <span className="font-medium text-blue-900">Explanation: </span>
                          <span className="text-blue-800">{question.explanation}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(question)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(question)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {questions.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No questions yet</h3>
              <p className="text-gray-600 mb-6">Start by adding questions to this quiz</p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={async () => {
                    setShowAIGenerate(true);
                    await testAIConnection();
                  }}
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate with AI
                </Button>
                <Button
                  onClick={() => {
                    setShowAddForm(true);
                    setEditingQuestion(null);
                    resetFormForNewQuestion();
                  }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Question Manually
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminQuestions;